import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";
import AddGraphicDesignModal from "../DesignStudio/addGraphicDesigner";
import GraphicDesignerPreviewMokup from "../DesignStudio/GraphicDesignerPreviewMokup";

const REACT_APP_IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

const GraphicDesignerDesigns = () => {
  const Navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // ✨ Match DesignStudio UI state
  const [statusFilter, setStatusFilter] = useState("All");
  const [buyerId, setBuyerId] = useState(
    localStorage.getItem("graphic_designer_id")
  ); // for highlight
  const [orderIds, setOrderIds] = useState(localStorage.getItem("order_id")); // for highlight

  // Modals, lightbox, etc.
  const [showModal, setShowModal] = useState(false);
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [orderId, setOrderId] = useState(0);

  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageList, setImageList] = useState([]);

  // Data
  const [loading, setLoading] = useState(true);
  const [rawDesignRows, setRawDesignRows] = useState([]);

  // fetch rows (one per design)
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/graphic_designer/order/products/design`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
          params: { designer_id: id },
        }
      );
      const rows = res?.data?.data || [];
      setRawDesignRows(rows.filter((r) => Number(r.designer_id) === id));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load designs");
      setRawDesignRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isFinite(id)) return;
    fetchData();
  }, [id]);

  // helpers
  const dedupeBy = (arr, keyFn) => {
    const seen = new Set();
    const out = [];
    for (const it of arr || []) {
      const k = keyFn(it);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(it);
      }
    }
    return out;
  };

  // Group rows by order, and collect media/designs
  const products = useMemo(() => {
    const grouped = {};
    for (const row of rawDesignRows) {
      const o = row.order || {};
      const key = o.order_id || row.order_id;

      if (!grouped[key]) {
        grouped[key] = {
          ...o, // keeps title, product, media_files, graphic_designer_id, etc.
          order_id: key,
          designs: [],
          media_files: dedupeBy(o.media_files || [], (m) => m.media_file),
        };
      }

      grouped[key].designs = dedupeBy(
        [
          ...grouped[key].designs,
          {
            design_url: row.design_url,
            status: row.status,
            designer_id: row.designer_id,
            graphic_design_id: row.graphic_design_id,
            created_at: row.created_at,
          },
        ],
        (d) => d.design_url
      );
    }
    return Object.values(grouped);
  }, [rawDesignRows]);

  // Match DesignStudio filters
  const getFilteredProducts = () => {
    if (statusFilter === "All") return products;
    if (statusFilter === "Pending")
      return products.filter((p) =>
        p.designs.every((d) => d.status === "pending")
      );
    if (statusFilter === "Assigned")
      return products.filter((p) => p.designs.length > 0);
    if (statusFilter === "Completed")
      return products.filter((p) =>
        p.designs.some((d) => d.status === "completed")
      );
    return products;
  };

  const filteredProducts = getFilteredProducts();

  // UI handlers (mirroring DesignStudio)
  const handleRowClick = (graphic_designer_id, order_id) => {
    const newId =
      buyerId === String(graphic_designer_id)
        ? null
        : String(graphic_designer_id);
    const newOrderId = orderIds === String(order_id) ? null : String(order_id);

    setBuyerId(newId);
    setOrderIds(newOrderId);

    if (newId) localStorage.setItem("graphic_designer_id", newId);
    else localStorage.removeItem("graphic_designer_id");

    if (newOrderId) localStorage.setItem("order_id", newOrderId);
    else localStorage.removeItem("order_id");
  };

  const handleOpenModal = (orderIdArg) => {
    setOrderId(orderIdArg);
    setShowModal(true);
  };

  const handleDesignOpenModal = (orderIdArg) => {
    setOrderId(orderIdArg);
    setShowDesignModal(true);
  };

  const handleMediaClick = (item) => {
    if (item.media_files?.length > 0) {
      setImageList(item.media_files);
      setSelectedImage(item.media_files[0]);
      setShowImagesModal(true);
    }
  };

  const handleDownloadAll = async (mediaFiles) => {
    if (!mediaFiles || mediaFiles.length === 0) return;
    const zip = new JSZip();
    const folder = zip.folder("images");
    await Promise.all(
      mediaFiles.map(async (file) => {
        try {
          const url = file.media_file.startsWith("http")
            ? file.media_file
            : `${REACT_APP_IMAGE_URL}${file.media_file}`;
          const response = await fetch(url);
          if (!response.ok) throw new Error("Failed to fetch image");
          const blob = await response.blob();
          const fileName = url.split("/").pop() || "image";
          folder.file(fileName, blob);
        } catch (err) {
          console.error("Download error:", err);
        }
      })
    );
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "images.zip");
  };

  if (loading)
    return (
      <div className="al">
        <div className="al-card" style={{ textAlign: "center", padding: "48px 20px", color: "var(--color-text-secondary)" }}>Loading...</div>
      </div>
    );

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Graphic Designer Designs</h1>
          <p className="al-sub">Designs submitted across this designer's assigned orders.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      {/* 🔹 Status filter tabs */}
      <div className="al-tabs">
        {["All", "Pending", "Assigned", "Completed"].map((status) => (
          <button
            key={status}
            className={`al-tab ${statusFilter === status ? "is-active" : ""}`}
            onClick={() => setStatusFilter(status)}
          >
            {status}
          </button>
        ))}
      </div>

      {/* 🔹 Designs table */}
      <div className="al-card">
      <div className="al-tablewrap">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>#</th>
            <th>Order ID</th>
            <th>Title</th>
            <th>User Media</th>
            <th>Designs</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((item, index) => (
              <tr
                key={item.order_id}
                onClick={() =>
                  handleRowClick(item.graphic_designer_id, item.order_id)
                }
                style={{
                  backgroundColor:
                    buyerId === String(item.graphic_designer_id) &&
                    orderIds === String(item.order_id)
                      ? "#e3f2fd"
                      : "transparent",
                  cursor: "pointer",
                }}
              >
                <td>{index + 1}</td>
                <td>{item.order_id}</td>
                <td>{item.title}</td>

                {/* User Media */}
                <td>
                  {item.media_files?.length > 0 ? (
                    <>
                      {item.media_files.map((media, i) => (
                        <img
                          key={`${media.media_file}_${i}`}
                          src={`${REACT_APP_IMAGE_URL}${media.media_file}`}
                          alt="media"
                          style={{
                            width: 40,
                            height: 40,
                            marginRight: 5,
                            cursor: "pointer",
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMediaClick(item);
                          }}
                        />
                      ))}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadAll(item.media_files);
                        }}
                        className="btn btn-sm btn-outline-primary p-1"
                        title="Download all media"
                      >
                        <FaDownload />
                      </button>
                    </>
                  ) : (
                    "No Media"
                  )}
                </td>

                {/* Designs */}
                <td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {item.designs?.slice(0, 3).map((d, i) => (
                      <img
                        key={`${d.design_url}_${i}`}
                        src={`${REACT_APP_IMAGE_URL}${d.design_url}`}
                        alt="design"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          cursor: "pointer",
                          objectFit: "cover",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDesignOpenModal(item.order_id);
                        }}
                      />
                    ))}
                    {item.designs?.length > 3 && (
                      <span>+{item.designs.length - 3}</span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(user?.id, item.order_id);
                      }}
                      className="btn btn-sm btn-outline-success"
                      title="Add / View all designs"
                    >
                      +
                    </button>
                  </div>
                </td>

                {/* Status (match DesignStudio semantics) */}
                <td>
                  {item.designs?.some((d) => d.status === "completed")
                    ? "Completed"
                    : item.designs?.length > 0
                    ? "Assigned"
                    : "Pending"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="text-center">
                No Record Found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      </div>

      {/* 🔹 Lightbox modal identical behavior */}
      {showImagesModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowImagesModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: 20,
              borderRadius: 10,
              maxWidth: "90%",
              maxHeight: "90%",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedImage && (
              <img
                src={`${REACT_APP_IMAGE_URL}${selectedImage.media_file}`}
                alt="full"
                style={{
                  maxWidth: "100%",
                  maxHeight: "60vh",
                  marginBottom: 16,
                }}
              />
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {imageList.map((img, i) => (
                <img
                  key={`${img.media_file}_${i}`}
                  src={`${REACT_APP_IMAGE_URL}${img.media_file}`}
                  alt="thumb"
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 6,
                    cursor: "pointer",
                    border:
                      selectedImage?.media_file === img.media_file
                        ? "2px solid #007bff"
                        : "1px solid #ccc",
                  }}
                  onClick={() => setSelectedImage(img)}
                />
              ))}
            </div>
            <button
              className="btn btn-danger"
              onClick={() => setShowImagesModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* 🔹 Modals (same props style as DesignStudio) */}
      {showModal && (
        <AddGraphicDesignModal
          show={showModal}
          setShow={setShowModal}
          fetchProducts={fetchData}
          orderId={orderId}
          onClose={() => setShowModal(false)}
        />
      )}
      {showDesignModal && (
        <GraphicDesignerPreviewMokup
          show={showDesignModal}
          userId={user.user_id}
          setShow={setShowDesignModal}
          orderId={orderId}
          onClose={() => setShowDesignModal(false)}
        />
      )}
    </div>
  );
};

export default GraphicDesignerDesigns;
